import { useState } from 'react';

interface BasicInfoData {
  name: string;
  phone: string;
  city: string;
  gender: string;
  kvkk: boolean;
  whatsappConsent: boolean;
}

const cities = ['İstanbul', 'Antalya', 'Kayseri', 'Ankara', 'İzmir', 'Bursa', 'Adana', 'Mersin'];

export default function BasicInfoForm({
  data,
  onChange,
  onSubmit,
}: {
  data: BasicInfoData;
  onChange: (d: BasicInfoData) => void;
  onSubmit: () => void;
}) {
  const [errors, setErrors] = useState<Record<string, string>>();

  const validate = () => {
    const e: Record<string, string> = {};
    if (!data.name.trim()) e.name = 'Ad soyad zorunludur';
    if (!data.phone.trim()) e.phone = 'Telefon numarası zorunludur';
    else if (data.phone.replace(/\D/g, '').length < 10) e.phone = 'Geçerli bir telefon numarası girin';
    if (!data.city) e.city = 'Şehir seçimi zorunludur';
    if (!data.gender) e.gender = 'Cinsiyet seçimi zorunludur';
    if (!data.kvkk) e.kvkk = 'KVKK onayı zorunludur';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit();
  };

  const update = (partial: Partial<BasicInfoData>) => {
    onChange({ ...data, ...partial });
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-dark-teal-50 flex items-center justify-center">
            <i className="ri-user-line text-dark-teal-500 text-lg" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-deep-navy-500">Temel Bilgiler</h2>
            <p className="text-xs text-gray-400">Adım 1/5 — Kimlik bilgilerinizi girin</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-deep-navy-400 mb-1.5">Ad Soyad</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Adınız ve soyadınız"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-deep-navy-500 placeholder:text-gray-300 focus:outline-none focus:border-dark-teal-300 transition-colors"
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-deep-navy-400 mb-1.5">Telefon Numarası</label>
            <input
              type="tel"
              value={data.phone}
              onChange={(e) => update({ phone: e.target.value })}
              placeholder="05XX XXX XX XX"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-deep-navy-500 placeholder:text-gray-300 focus:outline-none focus:border-dark-teal-300 transition-colors"
            />
            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-deep-navy-400 mb-1.5">Şehir</label>
            <select
              value={data.city}
              onChange={(e) => update({ city: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-deep-navy-500 bg-white focus:outline-none focus:border-dark-teal-300 transition-colors"
            >
              <option value="">Şehir seçin</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-deep-navy-400 mb-1.5">Cinsiyet</label>
            <div className="grid grid-cols-3 gap-2">
              {['Kadın', 'Erkek', 'Diğer'].map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => update({ gender: g })}
                  className={`py-3 rounded-xl text-sm font-medium border transition-all cursor-pointer ${
                    data.gender === g
                      ? 'border-dark-teal-500 bg-dark-teal-50 text-dark-teal-600'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
          </div>

          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.kvkk}
                onChange={(e) => update({ kvkk: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-dark-teal-500 focus:ring-dark-teal-500 cursor-pointer"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                <strong className="text-deep-navy-400">KVKK Aydınlatma Metni</strong>'ni okudum, kişisel verilerimin işlenmesine ve SpaProBridge platformunun gizlilik ilkelerine uygun şekilde kullanılmasına onay veriyorum.
              </span>
            </label>
            {errors.kvkk && <p className="text-xs text-red-500">{errors.kvkk}</p>}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.whatsappConsent}
                onChange={(e) => update({ whatsappConsent: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-dark-teal-500 focus:ring-dark-teal-500 cursor-pointer"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                WhatsApp üzerinden önemli güncellemeler, onay bildirimleri ve platform duyuruları almak istiyorum.
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="w-full py-3.5 rounded-full text-sm font-semibold text-white bg-dark-teal-500 hover:bg-dark-teal-600 transition-colors cursor-pointer mt-4"
          >
            Telefonumu Doğrula
          </button>
        </form>
      </div>
    </div>
  );
}